import { faSignInAlt } from '@fortawesome/pro-solid-svg-icons';
import { Formik, useFormikContext } from 'formik';
import { FC, useEffect, useState } from 'react';
import { Button } from 'src/components/button';
import { FormContainer } from 'src/components/form/form-container';
import { Input } from 'src/components/form/input';
import { Link } from 'src/components/link';
import { Modal, ModalDivider } from 'src/components/modal';
import { Stack } from 'src/components/stack';
import { ServerError, useAxios } from 'src/hooks/use-axios';
import { useCatchError } from 'src/hooks/use-catch-error';
import { useCognito } from 'src/hooks/use-cognito';
import { LoginResult, useLogin } from 'src/hooks/use-login';
import { useNotification } from 'src/hooks/use-notifications';
import { object, string } from 'yup';

const loginSchema = object().shape({
  email: string().required(),
  password: string().required(),
  token: string().required(),
});

const passwordRecoverySchema = object().shape({
  email: string().required(),
});

type Props = {
  onDismiss: () => void;
};

export const LoginErrorHandler: FC<{ error: ServerError | null }> = ({
  error,
}) => {
  const { setStatus } = useFormikContext();
  const notify = useNotification();

  useEffect(() => {
    if (!error) return;

    // if we encounter an error, we invalidate the whole form
    // see use-login hook for details about the error
    setStatus(true);
    notify({
      type: 'error',
      children: 'Please check your login details and try again.',
    });
  }, [error, notify, setStatus]);

  return null;
};

export function usePasswordRecovery() {
  const notify = useNotification();

  const passwordRecovery = useAxios(
    (axios, signal, data: { email: string }) =>
      axios.request<{ sentMail: boolean }>({
        signal,
        url: `${PRISMA_CONFIG.oldBackendUrl}/shipperUsers/forgotPassword`,
        method: 'POST',
        data,
      }),
    { neededOnPageLoad: false }
  );

  useEffect(() => {
    if (!passwordRecovery.response) return;

    if (passwordRecovery.response.data.sentMail) {
      notify({
        type: 'success',
        children: `If an account is associated with this address an e-mail containing instructions on how to reset your password has been sent to your e-mail-address.`,
      });
    } else {
      // note: according to the old platform this is an error case
      // see https://bitbucket.org/prismaplatform/pkp/src/26ba5364bc1f70567b08d26bda4028ca69b9563b/pkp-web-angular/src/main/webapp/app/includes/modalPasswordRecoveryContent.html#lines-27
      notify({
        type: 'error',
        children: 'An error occured, please try again later.',
      });
    }
  }, [notify, passwordRecovery.response]);

  return passwordRecovery;
}

async function redirectAfterLogin({ role }: LoginResult) {
  if (role === 'ADMIN' || role === 'SUPPORT_ADMIN') {
    location.href = `${PRISMA_CONFIG.monolithUrl}/admin/users.xhtml`;
  } else if (role === 'PRISMA_EDITOR') {
    location.href = `${PRISMA_CONFIG.monolithUrl}/admin/urgentNews.xhtml`;
  } else if (role === 'SSO_ADMIN') {
    location.href = `${PRISMA_CONFIG.oldFrontendUrl}/#/storage/offers`;
  } else {
    location.reload();
  }
}

export const LoginModal: FC<Props> = (props) => {
  const login = useLogin();
  const [passwordRecoveryMode, setPasswordRecoveryMode] = useState(false);
  const passwordRecovery = usePasswordRecovery();
  const { logout } = useCognito();
  const catchError = useCatchError();

  useEffect(() => {
    if (!login.response) return;

    const logoutExistingCognitoUserAndRedirect = async () => {
      // We need to kill potential old cognito users whenever someone tries to login with a non-migrated user.
      // Otherwise we would be authenticated twice: once in the platform, once in Cognito.
      try {
        await logout();
        redirectAfterLogin(login.response.data);
      } catch (error) {
        redirectAfterLogin(login.response.data);
      }
    };

    logoutExistingCognitoUserAndRedirect();
  }, [login.response, logout, catchError]);

  useEffect(() => {
    if (!passwordRecovery.response) return;

    setPasswordRecoveryMode(false);
  }, [passwordRecovery.response]);

  if (passwordRecoveryMode) {
    return (
      <Modal
        title="Password recovery"
        icon={faSignInAlt}
        size="small"
        {...props}
      >
        <Stack gap={1.5}>
          <Formik
            key="password-recovery"
            initialValues={{
              email: '',
            }}
            validationSchema={passwordRecoverySchema}
            onSubmit={passwordRecovery.execute}
          >
            <FormContainer mode="regular">
              <Input
                name="email"
                label="Email Address"
                placeholder="E.g. user@example.com"
                stacked
                autoFocus
              />

              <Stack flow="column" justifyContent="space-between">
                <Button
                  mode="secondary"
                  onClick={() => setPasswordRecoveryMode(false)}
                >
                  Cancel
                </Button>

                <Button type="submit" disabled={passwordRecovery.pending}>
                  {passwordRecovery.pending
                    ? 'Submitting...'
                    : 'Reset password'}
                </Button>
              </Stack>
            </FormContainer>
          </Formik>
        </Stack>
      </Modal>
    );
  } else {
    return (
      <Modal title="Login" icon={faSignInAlt} size="small" {...props}>
        <Stack gap={1.5}>
          <Formik
            // use key, so both forms are treated as standalone forms
            // (or the email validation will be the same between forms)
            key="login"
            initialValues={{
              email: '',
              password: '',
              token: '',
            }}
            validationSchema={loginSchema}
            onSubmit={login.execute}
          >
            <FormContainer mode="regular">
              <LoginErrorHandler error={login.error} />

              <Input
                name="email"
                label="Email Address"
                placeholder="E.g. user@example.com"
                stacked
                autoFocus
              />

              <Input
                name="password"
                type="password"
                label="Password"
                placeholder="E.g. Prisma_%20"
                stacked
              />

              <Input
                name="token"
                label="Token"
                placeholder="E.g. 123456"
                stacked
              />

              <Button
                type="submit"
                disabled={login.pending}
                data-testid="login"
              >
                {login.pending ? 'Submitting...' : 'Login'}
              </Button>

              <Stack justifyItems="center">
                <Button
                  mode="link"
                  onClick={() => setPasswordRecoveryMode(true)}
                >
                  Forgot Password?
                </Button>
              </Stack>
            </FormContainer>
          </Formik>

          <ModalDivider />

          <Stack justifyItems="center">
            Do not have an account?
            <Link to="/registration/wizard/start">
              <strong>Register Now</strong>
            </Link>
          </Stack>
        </Stack>
      </Modal>
    );
  }
};
